import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Play, Users, ChevronRight, ChevronLeft, RotateCcw, Pause, 
  Home, ShoppingBag, Store, Settings, Sparkles, AlertTriangle,
  CheckCircle2, User, Building2, UserCog, Eye, HelpCircle
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import { API_URL } from '../../utils/apiConfig';
import OwnerDemoGuide from './OwnerDemoGuide';

// ============ Persona Icons ============
const PERSONA_ICONS = {
  customer: User,
  beneficiary: Users,
  merchant: Store,
  admin: UserCog
};

const PERSONA_COLORS = {
  customer: 'blue',
  beneficiary: 'emerald',
  merchant: 'amber',
  admin: 'purple'
};

export default function InvestorDemoMode() {
  const navigate = useNavigate();
  const getToken = () => localStorage.getItem('pecash_token');
  
  // State
  const [loading, setLoading] = useState(true);
  const [demoStatus, setDemoStatus] = useState(null);
  const [walkthrough, setWalkthrough] = useState(null);
  const [currentStep, setCurrentStep] = useState(1);
  const [demoMode, setDemoMode] = useState('select'); // 'select', 'guided', 'explore'
  const [currentPersona, setCurrentPersona] = useState('customer');
  const [resetting, setResetting] = useState(false);
  const [showGuide, setShowGuide] = useState(false);

  // Fetch demo status
  useEffect(() => {
    const fetchStatus = async () => {
      const token = getToken();
      try {
        const [statusRes, walkthroughRes] = await Promise.all([
          fetch(`${API_URL}/api/admin/demo/status`, {
            headers: { 'Authorization': `Bearer ${token}` }
          }),
          fetch(`${API_URL}/api/admin/demo/walkthrough`, {
            headers: { 'Authorization': `Bearer ${token}` }
          })
        ]);
        
        if (statusRes.ok) {
          const data = await statusRes.json();
          setDemoStatus(data);
          setCurrentPersona(data.current_state?.current_persona || 'customer');
          setCurrentStep(data.current_state?.current_step || 1);
        }
        
        if (walkthroughRes.ok) {
          const data = await walkthroughRes.json();
          setWalkthrough(data);
        }
        
        setLoading(false);
      } catch (err) {
        console.error('Error fetching demo status:', err);
        toast.error('Failed to load demo mode');
        setLoading(false);
      }
    };
    
    fetchStatus();
  }, []);

  // Switch persona
  const switchPersona = useCallback(async (persona) => {
    const token = getToken();
    try {
      const response = await fetch(`${API_URL}/api/admin/demo/switch-persona`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ persona })
      });
      
      if (response.ok) {
        setCurrentPersona(persona);
        toast.success(`Switched to ${persona} persona`);
      }
    } catch (err) {
      console.error('Error switching persona:', err);
      toast.error('Failed to switch persona');
    }
  }, []);

  // Advance demo step
  const advanceStep = useCallback(async () => {
    const token = getToken();
    const currentStepData = walkthrough?.all_steps?.find(s => s.step === currentStep);
    
    try {
      const response = await fetch(`${API_URL}/api/admin/demo/advance`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          current_step: currentStep,
          action: currentStepData?.action || 'next'
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        setCurrentStep(data.advanced_to);
        setCurrentPersona(data.next_step_data?.persona);
        
        if (data.is_complete) {
          toast.success('Demo completed!');
        }
      }
    } catch (err) {
      console.error('Error advancing step:', err);
      toast.error('Failed to advance demo');
    }
  }, [currentStep, walkthrough]);

  // Go to specific step
  const goToStep = useCallback(async (step) => {
    const token = getToken();
    try {
      const response = await fetch(`${API_URL}/api/admin/demo/go-to-step/${step}`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setCurrentStep(step);
        setCurrentPersona(data.step_data?.persona);
      }
    } catch (err) {
      console.error('Error going to step:', err);
      toast.error('Failed to navigate to step');
    }
  }, []);

  // Reset demo
  const resetDemo = useCallback(async () => {
    const token = getToken();
    setResetting(true);
    try {
      const response = await fetch(`${API_URL}/api/admin/demo/reset`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ confirm: true })
      });
      
      if (response.ok) {
        setCurrentStep(1);
        setCurrentPersona('customer');
        toast.success('Demo reset to starting state');
      }
    } catch (err) {
      console.error('Error resetting demo:', err);
      toast.error('Failed to reset demo');
    } finally {
      setResetting(false);
    }
  }, []);

  // Get persona route
  const getPersonaRoute = (persona) => {
    switch (persona) {
      case 'customer': return '/dashboard';
      case 'beneficiary': return '/beneficiary/home';
      case 'merchant': return '/merchant/dashboard';
      case 'admin': return '/admin/dashboard';
      default: return '/';
    }
  };

  // Current step data
  const currentStepData = walkthrough?.all_steps?.find(s => s.step === currentStep);
  const PersonaIcon = PERSONA_ICONS[currentPersona] || User;
  const personaColor = PERSONA_COLORS[currentPersona] || 'blue';

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-center">
          <Sparkles className="w-8 h-8 text-purple-400 animate-pulse mx-auto mb-4" />
          <p className="text-slate-400">Loading Investor Demo...</p>
        </div>
      </div>
    );
  }

  if (!demoStatus?.available) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-center max-w-md">
          <AlertTriangle className="w-12 h-12 text-amber-400 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-white mb-2">Demo Mode Unavailable</h2>
          <p className="text-slate-400">{demoStatus?.reason || 'Demo mode is not available in this environment.'}</p>
          <Button
            onClick={() => navigate('/admin/dashboard')}
            className="mt-6 bg-purple-600 hover:bg-purple-700"
          >
            Back to Admin
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-900">
      {/* Demo Mode Selection */}
      {demoMode === 'select' && (
        <div className="min-h-screen flex items-center justify-center p-6">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="max-w-2xl w-full"
          >
            {/* Header */}
            <div className="text-center mb-12">
              <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-purple-600 to-blue-600 flex items-center justify-center mx-auto mb-6">
                <Sparkles className="w-10 h-10 text-white" />
              </div>
              <h1 className="text-4xl font-bold text-white mb-3">PeCash Investor Demo</h1>
              <div className="flex items-center justify-center gap-2 mb-4">
                <span className="px-3 py-1 bg-amber-900/30 border border-amber-700 rounded-full text-amber-400 text-sm">
                  DEMO ENVIRONMENT
                </span>
                <span className="px-3 py-1 bg-blue-900/30 border border-blue-700 rounded-full text-blue-400 text-sm">
                  SIMULATED TRANSACTIONS
                </span>
              </div>
              <p className="text-slate-400">
                No real money. No real notifications. Safe demo data only.
              </p>
            </div>

            {/* Mode Selection Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Guided Demo */}
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => setDemoMode('guided')}
                className="bg-gradient-to-br from-purple-900/50 to-slate-800 border border-purple-700/50 rounded-2xl p-8 text-left hover:border-purple-500 transition-colors group"
                data-testid="demo-start-guided-btn"
              >
                <div className="w-14 h-14 rounded-xl bg-purple-600 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  <Play className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">Start 5-Minute Demo</h3>
                <p className="text-slate-400 text-sm mb-4">
                  Guided walkthrough of the complete PeCash transaction flow.
                </p>
                <div className="flex items-center text-purple-400 text-sm font-medium">
                  Start Demo
                  <ChevronRight className="w-4 h-4 ml-1" />
                </div>
              </motion.button>

              {/* Explore Demo */}
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => setDemoMode('explore')}
                className="bg-gradient-to-br from-blue-900/50 to-slate-800 border border-blue-700/50 rounded-2xl p-8 text-left hover:border-blue-500 transition-colors group"
                data-testid="demo-start-explore-btn"
              >
                <div className="w-14 h-14 rounded-xl bg-blue-600 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  <Eye className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-xl font-bold text-white mb-2">Explore Demo</h3>
                <p className="text-slate-400 text-sm mb-4">
                  Freely switch between Customer, Beneficiary, Colmado, and Admin views.
                </p>
                <div className="flex items-center text-blue-400 text-sm font-medium">
                  Explore
                  <ChevronRight className="w-4 h-4 ml-1" />
                </div>
              </motion.button>
            </div>

            {/* Back button */}
            <div className="mt-8 text-center flex items-center justify-center gap-4">
              <Button
                variant="ghost"
                onClick={() => navigate('/admin/dashboard')}
                className="text-slate-400 hover:text-white"
              >
                Back to Admin Dashboard
              </Button>
              <Button
                variant="outline"
                onClick={() => setShowGuide(true)}
                className="border-purple-700 text-purple-400 hover:bg-purple-900/30"
                data-testid="demo-help-btn"
              >
                <HelpCircle className="w-4 h-4 mr-2" />
                Demo Guide
              </Button>
            </div>
          </motion.div>
        </div>
      )}

      {/* Owner Demo Guide Modal */}
      {showGuide && <OwnerDemoGuide onClose={() => setShowGuide(false)} />}

      {/* Guided Demo Mode */}
      {demoMode === 'guided' && (
        <div className="min-h-screen flex flex-col">
          {/* Top Navigation Bar */}
          <header className="bg-slate-800 border-b border-slate-700 px-6 py-4">
            <div className="max-w-7xl mx-auto flex items-center justify-between">
              <div className="flex items-center gap-4">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setDemoMode('select')}
                  className="text-slate-400 hover:text-white"
                >
                  <ChevronLeft className="w-4 h-4 mr-1" />
                  Exit
                </Button>
                <div className="h-6 w-px bg-slate-700" />
                <span className="text-white font-medium">Guided Demo</span>
                <span className="px-2 py-0.5 bg-purple-900/50 border border-purple-700 rounded text-purple-300 text-xs">
                  Step {currentStep} of {walkthrough?.total_steps || 13}
                </span>
              </div>
              
              <div className="flex items-center gap-3">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={resetDemo}
                  disabled={resetting}
                  className="text-slate-400 hover:text-white"
                  data-testid="demo-reset-btn"
                >
                  <RotateCcw className={`w-4 h-4 mr-1 ${resetting ? 'animate-spin' : ''}`} />
                  Reset
                </Button>
              </div>
            </div>
          </header>

          {/* Progress Bar */}
          <div className="bg-slate-800/50 px-6 py-2 border-b border-slate-700">
            <div className="max-w-7xl mx-auto flex items-center gap-1">
              {walkthrough?.all_steps?.map((step, i) => (
                <button
                  key={step.step}
                  onClick={() => goToStep(step.step)}
                  className={`flex-1 h-2 rounded-full transition-colors ${
                    step.step === currentStep 
                      ? 'bg-purple-500' 
                      : step.step < currentStep 
                        ? 'bg-purple-600/50' 
                        : 'bg-slate-700'
                  }`}
                />
              ))}
            </div>
            <div className="max-w-7xl mx-auto flex justify-between mt-2 text-xs text-slate-500">
              <span>Customer</span>
              <span>Beneficiary</span>
              <span>Colmado</span>
              <span>Admin</span>
            </div>
          </div>

          {/* Main Demo Content */}
          <main className="flex-1 p-6">
            <div className="max-w-4xl mx-auto">
              <AnimatePresence mode="wait">
                <motion.div
                  key={currentStep}
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="space-y-6"
                >
                  {/* Step Header */}
                  <div className="bg-slate-800 border border-slate-700 rounded-2xl p-6">
                    <div className="flex items-start gap-4">
                      <div className={`w-12 h-12 rounded-xl bg-${personaColor}-900/50 flex items-center justify-center flex-shrink-0`}>
                        <PersonaIcon className={`w-6 h-6 text-${personaColor}-400`} />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className={`px-2 py-0.5 bg-${personaColor}-900/30 border border-${personaColor}-700 rounded text-${personaColor}-300 text-xs uppercase`}>
                            {currentStepData?.persona}
                          </span>
                          <span className="text-slate-500 text-sm">Scene {currentStep}</span>
                        </div>
                        <h2 className="text-2xl font-bold text-white">{currentStepData?.title}</h2>
                        <p className="text-slate-400 mt-1">{currentStepData?.description}</p>
                      </div>
                    </div>
                  </div>

                  {/* Callout */}
                  {currentStepData?.callout && (
                    <div className="bg-purple-900/20 border border-purple-700/50 rounded-xl p-4">
                      <p className="text-purple-200 text-center font-medium">
                        "{currentStepData.callout}"
                      </p>
                    </div>
                  )}

                  {/* Demo Story Info */}
                  {currentStep <= 3 && (
                    <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4">
                      <h4 className="text-white font-medium mb-3">Demo Transaction</h4>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div>
                          <span className="text-slate-500">Customer</span>
                          <p className="text-white font-medium">{demoStatus?.personas?.customer?.name}</p>
                        </div>
                        <div>
                          <span className="text-slate-500">Beneficiary</span>
                          <p className="text-white font-medium">{demoStatus?.personas?.beneficiary?.name}</p>
                        </div>
                        <div>
                          <span className="text-slate-500">Colmado</span>
                          <p className="text-white font-medium">{demoStatus?.personas?.merchant?.name}</p>
                        </div>
                        <div>
                          <span className="text-slate-500">Amount</span>
                          <p className="text-white font-medium">${walkthrough?.demo_story?.purchase_amount_usd}</p>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Navigation Action */}
                  <div className="flex items-center justify-between pt-4">
                    <Button
                      variant="ghost"
                      onClick={() => currentStep > 1 && goToStep(currentStep - 1)}
                      disabled={currentStep === 1}
                      className="text-slate-400"
                    >
                      <ChevronLeft className="w-4 h-4 mr-1" />
                      Back
                    </Button>

                    <Button
                      onClick={advanceStep}
                      disabled={currentStep >= (walkthrough?.total_steps || 13)}
                      className="bg-purple-600 hover:bg-purple-700"
                      data-testid="demo-next-btn"
                    >
                      {currentStep >= (walkthrough?.total_steps || 13) ? (
                        <>
                          <CheckCircle2 className="w-4 h-4 mr-2" />
                          Complete
                        </>
                      ) : (
                        <>
                          Next
                          <ChevronRight className="w-4 h-4 ml-1" />
                        </>
                      )}
                    </Button>
                  </div>

                  {/* View Live */}
                  <div className="text-center">
                    <Button
                      variant="outline"
                      onClick={() => {
                        const route = getPersonaRoute(currentPersona);
                        navigate(route);
                      }}
                      className="border-slate-600 text-slate-300 hover:bg-slate-800"
                    >
                      <Eye className="w-4 h-4 mr-2" />
                      View {currentStepData?.persona} Screen
                    </Button>
                  </div>
                </motion.div>
              </AnimatePresence>
            </div>
          </main>
        </div>
      )}

      {/* Explore Demo Mode */}
      {demoMode === 'explore' && (
        <div className="min-h-screen flex flex-col">
          {/* Top Navigation Bar */}
          <header className="bg-slate-800 border-b border-slate-700 px-6 py-4">
            <div className="max-w-7xl mx-auto flex items-center justify-between">
              <div className="flex items-center gap-4">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setDemoMode('select')}
                  className="text-slate-400 hover:text-white"
                >
                  <ChevronLeft className="w-4 h-4 mr-1" />
                  Exit
                </Button>
                <div className="h-6 w-px bg-slate-700" />
                <span className="text-white font-medium">Explore Demo</span>
              </div>
              
              <div className="flex items-center gap-3">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={resetDemo}
                  disabled={resetting}
                  className="text-slate-400 hover:text-white"
                >
                  <RotateCcw className={`w-4 h-4 mr-1 ${resetting ? 'animate-spin' : ''}`} />
                  Reset
                </Button>
              </div>
            </div>
          </header>

          {/* Main Content */}
          <main className="flex-1 p-6">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-2xl font-bold text-white mb-2 text-center">Choose a Persona</h2>
              <p className="text-slate-400 text-center mb-8">
                Switch between different user views to explore the PeCash experience
              </p>

              {/* Persona Grid */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {Object.entries(demoStatus?.personas || {}).map(([key, persona]) => {
                  const Icon = PERSONA_ICONS[key] || User;
                  const color = PERSONA_COLORS[key] || 'blue';
                  const isActive = currentPersona === key;
                  
                  return (
                    <motion.button
                      key={key}
                      whileHover={{ scale: 1.03 }}
                      whileTap={{ scale: 0.97 }}
                      onClick={() => {
                        switchPersona(key);
                        navigate(getPersonaRoute(key));
                      }}
                      className={`relative p-6 rounded-2xl border transition-all ${
                        isActive 
                          ? `bg-${color}-900/30 border-${color}-600` 
                          : 'bg-slate-800 border-slate-700 hover:border-slate-500'
                      }`}
                      data-testid={`demo-persona-${key}`}
                    >
                      {isActive && (
                        <div className="absolute -top-2 -right-2 w-5 h-5 bg-green-500 rounded-full flex items-center justify-center">
                          <CheckCircle2 className="w-3.5 h-3.5 text-white" />
                        </div>
                      )}
                      <div className={`w-12 h-12 rounded-xl bg-${color}-900/50 flex items-center justify-center mx-auto mb-3`}>
                        <Icon className={`w-6 h-6 text-${color}-400`} />
                      </div>
                      <h3 className="text-white font-medium text-center">{persona.name}</h3>
                      <p className="text-slate-500 text-xs text-center mt-1 capitalize">{persona.role}</p>
                    </motion.button>
                  );
                })}
              </div>

              {/* Demo Data Info */}
              <div className="mt-8 bg-slate-800/50 border border-slate-700 rounded-xl p-6">
                <h3 className="text-white font-medium mb-4">Demo Authorization</h3>
                {demoStatus?.authorization && (
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                    <div>
                      <span className="text-slate-500">USD Amount</span>
                      <p className="text-white font-medium">${demoStatus.authorization.usd_amount}</p>
                    </div>
                    <div>
                      <span className="text-slate-500">Service Fee</span>
                      <p className="text-purple-400 font-medium">${demoStatus.authorization.service_fee_usd}</p>
                    </div>
                    <div>
                      <span className="text-slate-500">Customer Total</span>
                      <p className="text-emerald-400 font-medium">${demoStatus.authorization.customer_total_usd}</p>
                    </div>
                    <div>
                      <span className="text-slate-500">DOP Available</span>
                      <p className="text-white font-medium">RD${demoStatus.authorization.dop_remaining_amount?.toLocaleString()}</p>
                    </div>
                    <div>
                      <span className="text-slate-500">FX Rate</span>
                      <p className="text-white">{demoStatus.authorization.fx_rate_applied} DOP/USD</p>
                    </div>
                    <div>
                      <span className="text-slate-500">Status</span>
                      <p className="text-emerald-400 capitalize">{demoStatus.authorization.status}</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Quick Actions */}
              <div className="mt-6 flex flex-wrap justify-center gap-3">
                <Button
                  variant="outline"
                  onClick={() => setDemoMode('guided')}
                  className="border-slate-600 text-slate-300"
                >
                  <Play className="w-4 h-4 mr-2" />
                  Start Guided Demo
                </Button>
                <Button
                  variant="outline"
                  onClick={() => navigate('/admin/pricing-simulator')}
                  className="border-slate-600 text-slate-300"
                >
                  <Settings className="w-4 h-4 mr-2" />
                  Pricing Simulator
                </Button>
              </div>
            </div>
          </main>
        </div>
      )}
    </div>
  );
}
